import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { usePrivy } from "@privy-io/react-auth";
import { useStats, useVip, useClaimCashback } from "@/lib/hooks/useApi";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import ResourceBar from "@/components/ResourceBar";
import { HowItWorkButton } from "@/components/ui/HowItWorkButton";
import { useNextStep } from "@/components/NextStepProvider";
import {
  cn,
  USDC_ICON,
  generateTextShadow,
  pixelCornerClipPath,
} from "@/lib/utils";
import { t } from "@/lib/i18n";
import {
  transformGroups,
  transformUserState,
  transformCashback,
  type VipUserState,
  type VipCashback,
  type VipTier,
} from "@/lib/vip-data";
import { RevealOverlay } from "@/components/RevealOverlay";
import { Spinner } from "@/components/ui/spinner";
import { CashbackSection } from "./-components/CashbackSection";
import { VipTiersAccordion } from "./-components/VipTiersAccordion";
import { VipLeagueHeader } from "./-components/VipLeagueHeader";
import { DailyTrophyBonusButton } from "@/components/DailyTrophyBonusButton";

export const Route = createFileRoute("/(game)/vip-program/")({
  component: VipProgramPage,
});

function VipProgramPage() {
  const navigate = useNavigate();
  const { user, ready, authenticated } = usePrivy();
  const authWalletAddress = user?.wallet?.address;
  const { data: statsData } = useStats(authWalletAddress ?? "");

  const isAuthenticated = ready && authenticated;
  const userId = statsData?.userId;
  const { startNextStep, closeNextStep } = useNextStep();

  const scrollRef = useRef<HTMLDivElement>(null);

  const handleStartTour = useCallback(() => {
    try {
      closeNextStep();
    } catch {}
    setExpandedTiers(new Set());
    scrollRef.current?.scrollTo({ top: 0 });
    startNextStep("vipProgramTour");
  }, [closeNextStep, startNextStep]);

  // Fetch VIP data from BE
  const { data: vipData, isLoading } = useVip(userId);
  const { mutateAsync: claimCashback } = useClaimCashback();

  // Transform BE response → FE types
  const tiers: VipTier[] = useMemo(
    () => (vipData ? transformGroups(vipData.groups) : []),
    [vipData],
  );

  const vipUser: VipUserState | null = useMemo(
    () =>
      vipData?.user ? transformUserState(vipData.user, vipData.groups) : null,
    [vipData],
  );

  const cashback: VipCashback | null = useMemo(
    () =>
      vipData?.user ? transformCashback(vipData.user, vipData.groups) : null,
    [vipData],
  );

  const [revealBonus, setRevealBonus] = useState<{
    type: string;
    amount: number;
  } | null>(null);
  const [claimingType, setClaimingType] = useState<string | null>(null);

  const [expandedTiers, setExpandedTiers] = useState<Set<string>>(new Set());

  const toggleTier = (tierName: string) => {
    setExpandedTiers((prev) => {
      const next = new Set(prev);
      if (next.has(tierName)) {
        next.delete(tierName);
      } else {
        next.add(tierName);
      }
      return next;
    });
  };

  const handleClaimBonus = async (
    type: "instant" | "daily" | "weekly" | "monthly",
    amount: number,
  ) => {
    setClaimingType(type);
    try {
      const result = await claimCashback(type);
      setRevealBonus({ type, amount: result.amountUsdc });
    } catch (error) {
      console.error("Claim failed:", error);
    } finally {
      setClaimingType(null);
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-[#1D2A52] relative">
      {/* ─── Fixed: Nav overlay ─── */}
      <FloatingLeftButton
        onClick={() => navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />
      <ResourceBar
        showBalance={true}
        showMenu
        className="absolute top-4 left-10 right-4 flex z-20 pl-3"
        rightExtra={
          <HowItWorkButton onClick={handleStartTour} size="size-8!" />
        }
      />

      {/* ─── Fixed: League header with badges + progress ─── */}
      <div className="flex flex-col shrink-0 pt-14 bg-[#1D2A52]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <div data-step="vip-tiers">
            {/* <div className="ms-2 mt-2"> */}
            {/*   <ShinyText text={"VIP Program"} className="text-xl" /> */}
            {/* </div> */}
            <div className="ms-2 mt-2 me-2 flex items-center justify-between">
              <h3 className="VIP text-2xl">{t("vipProgram.vip")}</h3>
              <DailyTrophyBonusButton source="vip" />
            </div>
            <VipLeagueHeader vipUser={vipUser} tiers={tiers} />
          </div>
        )}
        <div
          data-step={vipUser ? "vip-progress" : undefined}
          className={cn(
            "relative w-full bg-[#0659b5] z-1 -mt-1 overflow-hidden",
            vipUser ? "h-5" : "h-1",
          )}
        >
          {vipUser && (
            <>
              <motion.div
                className="absolute inset-0"
                style={{
                  backgroundColor:
                    tiers.find((l) => l.name === vipUser.currentTier)?.color ??
                    "#0659b5",
                  clipPath: pixelCornerClipPath(1.25, ["tr", "br"]),
                }}
                initial={{ width: 0 }}
                animate={{ width: `${vipUser.progressPercent}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
              <span
                className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white"
                style={{
                  textShadow: generateTextShadow(
                    0.5,
                    tiers.find((l) => l.name === vipUser.currentTier)?.stroke,
                  ),
                }}
              >
                {vipUser.xp.toLocaleString()} /{" "}
                {vipUser.nextTierXp.toLocaleString()} XP
                {vipUser.nextSubTierName && (
                  <span className="ms-1.5 text-white">
                    {" "}
                    → {vipUser.nextSubTierName}
                  </span>
                )}
              </span>
            </>
          )}
        </div>
      </div>

      {/* ─── Scrollable content ─── */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto pt-6 px-2 pb-3 space-y-3"
      >
        {!isLoading && (
          <>
            <div data-step="vip-cashback">
              <CashbackSection
                cashback={cashback}
                currentTier={vipUser?.currentTier ?? null}
                claimingType={claimingType}
                onClaimBonus={handleClaimBonus}
                isAuthenticated={isAuthenticated}
                tiers={tiers}
              />
            </div>

            <div data-step="vip-accordion">
              <VipTiersAccordion
                currentTier={vipUser?.currentTier ?? null}
                expandedTiers={expandedTiers}
                onToggleTier={toggleTier}
                tiers={tiers}
              />
            </div>
          </>
        )}
      </div>

      {/* Reveal overlay on claim success */}
      {revealBonus && (
        <RevealOverlay
          isOpen={!!revealBonus}
          onClose={() => setRevealBonus(null)}
          title={t("vipProgram.cashback.claimed")}
          rewardText={`${revealBonus.type.charAt(0).toUpperCase() + revealBonus.type.slice(1)} Cashback`}
          rewardValue={revealBonus.amount}
          buttonText={t("vipProgram.cashback.continue")}
          description={t("vipProgram.cashback.addedToBalance", {
            amount: String(revealBonus.amount),
          })}
          shouldUseRewardIcons={false}
          alternatePrizeImageSrc={USDC_ICON}
          alternatePrizeImageSrcSize="w-20"
          alternatePrizeText={`+$${revealBonus.amount}`}
        />
      )}
    </div>
  );
}
